// This browser's side of web push and of the desktop app's destination: the
// browser's APIs (Notification, the service worker, PushManager) and the REST
// calls that go with them. Screens reach them through queries.ts.
//
// Promise:
// - Subscribing asks for the permission, subscribes, registers the
//   subscription and remembers its endpoint on this device ("this browser
//   chose to receive"). A remembered endpoint whose subscription vanished
//   (iPhone home screen apps lose it while the permission stays) is
//   subscribed again silently when the app opens; the old row is removed.
// - Turning it off or signing out forgets that choice first, so a browser that
//   was turned off is never turned on again by itself.
// - Unsubscribing removes the server's row and always undoes the browser's
//   subscription, even when the server fails (otherwise the next person on
//   this device would get the previous one's notices).
// - Before signing out nothing throws and nothing waits longer than a few
//   seconds: signing out always goes on.
// - The desktop app registers its installation instead of a subscription, on
//   by default; turning it off there is remembered the same way. So does the
//   iOS app, with its APNs token, once the person allowed its notifications.
import type {
  PushState,
  PutAppPushDevice,
  PutNoticeDesktop,
  PutPushSubscription,
} from "@trameria/cloud-api/notices-shared";
import {
  type Api,
  type AppNoticeHost,
  appNoticeHost,
  noticeDesktop,
  readAppNotices,
  type ShellNoticesState,
} from "@trameria/ui-core";

export type BrowserPushState =
  /** No push here; `ios`: a home screen app would have it. */
  | { kind: "unsupported"; ios: boolean }
  | { kind: "denied" }
  | { kind: "unsubscribed" }
  | { kind: "subscribed"; endpoint: string };

export const SW_URL = "/sw.js";
const SW_READY_TIMEOUT_MS = 10_000;
export const SIGN_OUT_TIMEOUT_MS = 3_000;
const BROWSER_UNSUBSCRIBE_WAIT_MS = 1_000;
/** The desktop's name in the list of destinations (a product name, not translated). */
export const DESKTOP_LABEL = "trameria desktop";

// Kept under the names the previous web used, so a browser keeps its choice.
const ENDPOINT_KEY = "trameria.push.endpoint";
const DESKTOP_OFF_KEY = "trameria.push.desktop";
const APP_OFF_KEY = "trameria.push.app";

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
};

const withTimeout = <T>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });

export const rememberedEndpoint = () => read(ENDPOINT_KEY);
export const rememberEndpoint = (endpoint: string) =>
  write(ENDPOINT_KEY, endpoint);

function isIos(): boolean {
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

const pushSupported = () =>
  typeof window !== "undefined" &&
  "PushManager" in window &&
  "serviceWorker" in navigator &&
  typeof Notification !== "undefined";

const granted = () =>
  typeof Notification !== "undefined" && Notification.permission === "granted";

/** The registration there is now (never waits for one). */
const currentRegistration = () =>
  navigator.serviceWorker.getRegistration().catch(() => undefined);

/** An active registration, registering again when the one at start failed. */
async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const registration =
    (await currentRegistration()) ??
    (await navigator.serviceWorker.register(SW_URL));
  if (registration.active) return registration;
  const ready = await withTimeout<ServiceWorkerRegistration | null>(
    navigator.serviceWorker.ready,
    SW_READY_TIMEOUT_MS,
    null,
  );
  if (!ready) throw new Error("the service worker did not start");
  return ready;
}

/** Once at start. Nothing happens where there are no service workers. */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  void navigator.serviceWorker.register(SW_URL).catch(() => {});
}

/** A notification clicked in the service worker says where to go. Returns stop. */
export function listenServiceWorkerNavigate(
  go: (url: string) => void,
): () => void {
  if (!("serviceWorker" in navigator)) return () => {};
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { type?: unknown; url?: unknown } | null;
    if (data?.type === "navigate" && typeof data.url === "string") go(data.url);
  };
  navigator.serviceWorker.addEventListener("message", onMessage);
  return () =>
    navigator.serviceWorker.removeEventListener("message", onMessage);
}

/** What this browser can do and has done (not the server's view). */
export async function readBrowserPush(): Promise<BrowserPushState> {
  if (!pushSupported()) return { kind: "unsupported", ios: isIos() };
  if (Notification.permission === "denied") return { kind: "denied" };
  const registration = await currentRegistration();
  const subscription = await registration?.pushManager
    .getSubscription()
    .catch(() => null);
  return subscription
    ? { kind: "subscribed", endpoint: subscription.endpoint }
    : { kind: "unsubscribed" };
}

/** Asks for the permission, then subscribes and registers. */
export async function subscribeBrowser(
  api: Api,
  publicKey: string,
): Promise<string> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("permission was not granted");
  return subscribeAndRegister(api, publicKey);
}

/**
 * Subscribes again, without asking, when this browser chose to receive, the
 * permission stays and the subscription is gone. Null when nothing was done.
 */
export async function restoreLostSubscription(
  api: Api,
  publicKey: string,
): Promise<string | null> {
  if (!pushSupported() || rememberedEndpoint() === null || !granted())
    return null;
  const registration = await currentRegistration();
  if (await registration?.pushManager.getSubscription()) return null;
  return subscribeAndRegister(api, publicKey);
}

/** Whether a lost subscription may be restored (what can be known at once). */
export const mayRestore = () => rememberedEndpoint() !== null && granted();

async function subscribeAndRegister(
  api: Api,
  publicKey: string,
): Promise<string> {
  const registration = await activeRegistration();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });
  const json = subscription.toJSON();
  const body: PutPushSubscription = {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
    userAgent: navigator.userAgent.slice(0, 500),
  };
  await api("/api/me/push/subscriptions", { method: "PUT", body });
  const previous = rememberedEndpoint();
  rememberEndpoint(subscription.endpoint);
  // The push service keeps accepting the old endpoint, so its row would stay.
  if (previous && previous !== subscription.endpoint)
    await forgetOnServer(api, previous).catch(() => {});
  return subscription.endpoint;
}

async function forgetOnServer(api: Api, endpoint: string): Promise<void> {
  const state = await api<PushState>("/api/me/push");
  const row = state.subscriptions.find((s) => s.endpoint === endpoint);
  if (row)
    await api(`/api/me/push/subscriptions/${encodeURIComponent(row.id)}`, {
      method: "DELETE",
    });
}

/** Turns this browser off: forgets the choice, removes the row, unsubscribes. */
export async function unsubscribeBrowser(api: Api): Promise<void> {
  write(ENDPOINT_KEY, null);
  if (!("serviceWorker" in navigator)) return;
  const registration = await currentRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  try {
    await forgetOnServer(api, subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}

// ---- the desktop app's destination ---------------------------------------------

export const desktopTurnedOff = () => read(DESKTOP_OFF_KEY) === "off";

export function registerDesktop(api: Api, installationId: string) {
  const body: PutNoticeDesktop = { installationId, label: DESKTOP_LABEL };
  return api<{ id: string }>("/api/me/push/desktop", { method: "PUT", body });
}

export function enableDesktop(api: Api, installationId: string) {
  write(DESKTOP_OFF_KEY, null);
  return registerDesktop(api, installationId);
}

/** Marks it off first, so a failure does not register it again at the next start. */
export async function disableDesktop(api: Api, installationId: string) {
  write(DESKTOP_OFF_KEY, "off");
  await api(`/api/me/push/desktop/${encodeURIComponent(installationId)}`, {
    method: "DELETE",
  });
}

// ---- the iOS app's destination ---------------------------------------------------

export const appTurnedOff = () => read(APP_OFF_KEY) === "off";

/** Registers this app's token (the app says it is allowed and has one). */
export function registerApp(
  api: Api,
  host: AppNoticeHost,
  state: ShellNoticesState,
) {
  if (state.status !== "authorized" || !state.token)
    return Promise.reject(new Error("notifications are not allowed"));
  const body: PutAppPushDevice = {
    installationId: host.notices.installationId,
    token: state.token,
    environment: state.environment,
    label: host.notices.device === "iPad" ? "iPad" : "iPhone",
  };
  return api<{ id: string }>("/api/me/push/app", { method: "PUT", body });
}

/**
 * Turns this app on: asks the app to ask the person (or, refused before, to
 * open its notification settings), then registers what it was allowed.
 * Resolves with how the app's notifications stand after that.
 */
export async function enableApp(
  api: Api,
  host: AppNoticeHost,
): Promise<ShellNoticesState | null> {
  const state = await readAppNotices(host, { ask: true });
  if (state?.status === "authorized" && state.token) {
    write(APP_OFF_KEY, null);
    await registerApp(api, host, state);
  }
  return state;
}

/** Marks it off first, so a failure does not register it again at the next start. */
export async function disableApp(api: Api, installationId: string) {
  write(APP_OFF_KEY, "off");
  await api(`/api/me/push/app/${encodeURIComponent(installationId)}`, {
    method: "DELETE",
  });
}

// ---- signing out -----------------------------------------------------------------

/**
 * Before signing out: this browser's subscription and this desktop's row go
 * away. Never throws; waits at most `timeoutMs` for the server, and undoes the
 * browser's subscription even when the server did not answer.
 */
export async function forgetDeviceBeforeSignOut(
  api: Api,
  timeoutMs = SIGN_OUT_TIMEOUT_MS,
): Promise<void> {
  write(ENDPOINT_KEY, null);
  const installationId = noticeDesktop()?.installationId;
  const appId = appNoticeHost()?.notices.installationId;
  // Read on this device first, so the browser is unsubscribed even when the
  // server never answers.
  const subscription =
    "serviceWorker" in navigator
      ? await withTimeout(
          currentRegistration().then(
            (r) => r?.pushManager.getSubscription() ?? null,
          ),
          BROWSER_UNSUBSCRIBE_WAIT_MS,
          null,
        )
      : null;
  const server = async () => {
    if (installationId)
      await api(`/api/me/push/desktop/${encodeURIComponent(installationId)}`, {
        method: "DELETE",
      }).catch(() => {});
    if (appId)
      await api(`/api/me/push/app/${encodeURIComponent(appId)}`, {
        method: "DELETE",
      }).catch(() => {});
    if (subscription) await forgetOnServer(api, subscription.endpoint);
  };
  await withTimeout(server(), timeoutMs, undefined);
  if (subscription)
    await withTimeout(
      subscription.unsubscribe().then(() => undefined),
      BROWSER_UNSUBSCRIBE_WAIT_MS,
      undefined,
    );
}

/** The VAPID public key (base64url) as applicationServerKey wants it. */
export function base64UrlToBytes(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
